<script lang="ts" setup>
import { User } from '@/models/user.model';
import { Actions, ShiftCMSState } from '@/store';
import { computed, onMounted, Ref, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AppLanguage from './AppLanguage.vue';
import { UsersActions } from '@/stores/users.store';

const store = useStore<ShiftCMSState>();
const router = useRouter();
const user = computed<User | null>(() => store.state.loggedInUser);
const version = computed<string>(() => store.state.version);

/*
  This component show the logged in user, but also functions as a way to force
  The CMS to verify if the user is properly logged in
*/

onMounted(() => {
  store.dispatch(Actions.GetCurrentUser);
  store.dispatch(Actions.GetVersion);
});

const logout = () => {
  store.dispatch(Actions.Logout);
  router.push('/login');
};

const qrCode = ref<string>('');
const digits = ref<string>('');
const message = ref<string>('');

const enable2FA = async () => {
  const result = await store.dispatch(UsersActions.Enable2FA);

  qrCode.value = result.qrCodeDataUrl;
};

const verify2FA = async (isActive: Ref) => {
  const result = await store.dispatch(UsersActions.Verify2FA, digits.value);
  if (result.twoFactorEnabled) {
    isActive.value = false;
    store.dispatch(Actions.GetCurrentUser);
    message.value = '';
    store.dispatch(Actions.ShowNotification, {
      message: result.message,
      type: 'success',
    });
  } else {
    message.value = result.message;
  }
};

const disable2FA = async () => {
  const result = await store.dispatch(UsersActions.Disable2FA, user.value?.id);

  if (result.twoFactorDisabled) {
    store.dispatch(Actions.GetCurrentUser);
    message.value = '';
    store.dispatch(Actions.ShowNotification, {
      message: result.message,
      type: 'success',
    });
  } else {
    message.value = result.message;
  }
};
</script>
<template>
  <AppLanguage />
  <div class="absolute version">
    <div class="label">Version</div>
    <div class="version-value">{{ version }}</div>
  </div>
  <div class="absolute" v-if="user">
    <div class="label">Logged in</div>
    <v-menu>
      <template v-slot:activator="{ props }">
        <a href="#" @click.prevent v-bind="props" class="user-name"
          >{{ user.firstName }} {{ user.lastName }}</a
        >
      </template>
      <v-list>
        <v-list-item>
          <v-btn block compact variant="outlined" outlined @click="logout"
            >Log out
          </v-btn>
        </v-list-item>
        <v-list-item>
          <v-dialog width="auto" v-if="!user.twoFactorEnabled">
            <template v-slot:activator="{ props: activatorProps }">
              <v-btn
                v-bind="activatorProps"
                class="mt-2"
                type="button"
                block
                color="info"
                @click="enable2FA"
                >Enable 2FA</v-btn
              >
            </template>
            <template v-slot:default="{ isActive }">
              <v-card>
                <v-card-text>
                  <h2>Enable Two Factor Authentication</h2>
                  <p>
                    <strong>Scan</strong> the following QR code with your
                    authenticator app.
                  </p>
                  <img
                    :src="qrCode"
                    alt="2FA QR code"
                    style="margin: 0 auto; display: block"
                  />
                  <p>
                    Provide the 6 digit code and enable two factor
                    authentication on your account.
                  </p>
                  <v-row>
                    <v-col
                      ><v-text-field v-model="digits" label="6 Digit Code"
                    /></v-col>
                    <v-col>
                      <v-btn
                        class="mt-2"
                        color="success"
                        type="button"
                        block
                        @click="() => verify2FA(isActive)"
                        >Enable 2FA</v-btn
                      ></v-col
                    >
                  </v-row>
                  <v-alert color="warning" v-if="message">{{
                    message
                  }}</v-alert>
                </v-card-text>
              </v-card>
            </template>
          </v-dialog>
          <v-btn
            v-else
            class="mt-2"
            color="warning"
            type="button"
            block
            @click="disable2FA"
            >Disable 2FA</v-btn
          >
        </v-list-item>
        <v-list-item v-if="message">
          <v-alert color="warning">{{ message }}</v-alert>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>
<style scoped>
.absolute {
  position: fixed;
  z-index: 150;
  right: 1em;
  top: 0.9em;
  width: 150px;
}

.version {
  width: auto;
  text-align: right;
  right: calc(2em + 150px);
}

.version-value {
  color: gray;
}

.label {
  font-size: 0.5em;
}
.user-name {
  color: gray;
  text-decoration: none;
}
.user-name:hover {
  color: black;
}
</style>
